import { isEqual, isNil } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import useEntityFormContext from '../containers/EntityForm/internal/useEntityFormContext';
import Spinner from '../utils/Spinner';
import SelectBox, { RETURN_TYPE } from './base/SelectBox';

const FormSelectBox = ({
  meta: { touched, submitFailed, error, dirty, submitting },
  input: { value, onChange },
  disabled,
  loading,
  defaultValue,
  options,
  itemValuePropName,
  isEmptyValueAllowed,
  emptyValue,
  returnType,
  required,
  labelRequired,
  ...rest
}) => {
  const errorMessage = submitFailed || touched ? error : null;

  const { formName, isNew } = useEntityFormContext();

  const _defaultValue = useMemo(() => {
    if (!isNil(defaultValue)) {
      return defaultValue;
    }
    if (options?.length === 1 && (isNew || required)) {
      return returnType === RETURN_TYPE.OPTION
        ? options[0]
        : options[0][itemValuePropName];
    }
  }, [isNew, returnType, defaultValue, itemValuePropName, options, required]);

  const selectedValue = useMemo(
    () =>
      (!isNil(value) && value !== '') || dirty || touched
        ? value
        : _defaultValue,
    [_defaultValue, dirty, touched, value],
  );

  useEffect(() => {
    if (
      (isNil(value) || value === '') &&
      !dirty &&
      !touched &&
      !isNil(_defaultValue)
    ) {
      onChange(selectedValue);
    }

    if (
      isEqual(value, emptyValue) &&
      (dirty || touched) &&
      !isEmptyValueAllowed &&
      !isNil(_defaultValue)
    ) {
      onChange(_defaultValue);
    }
  }, [
    _defaultValue,
    dirty,
    touched,
    onChange,
    selectedValue,
    value,
    emptyValue,
    isEmptyValueAllowed,
  ]);

  if (loading) {
    return <Spinner inline={false} />;
  }

  return (
    <SelectBox
      formElement
      disabled={disabled || submitting}
      emptyValue={emptyValue}
      errorMessage={errorMessage}
      formId={formName}
      itemValuePropName={itemValuePropName}
      labelRequired={labelRequired}
      options={options}
      required={required}
      returnType={returnType}
      value={selectedValue}
      onChange={onChange}
      {...rest}
    />
  );
};

FormSelectBox.propTypes = {
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),

  meta: PropTypes.shape({
    touched: PropTypes.bool,
    submitFailed: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
  input: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
      PropTypes.object,
    ]),
  }).isRequired,
  isEmptyValueAllowed: PropTypes.bool,
  emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

FormSelectBox.defaultProps = {
  disabled: false,
  loading: false,
  defaultValue: null,
  isEmptyValueAllowed: true,
  emptyValue: '',
};

export default FormSelectBox;
